import axios from 'axios';
import React, { Fragment, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSpinner, FaImage, FaEdit, FaSave } from "react-icons/fa";
import LoadingScreen from '../../components/LoadingScreen';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

function EditCategoryPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [categoryData, setCategoryData] = useState({ name: '', image: '' });
    const [imageUploading, setImageUploading] = useState(false);
    const [savingCategory, setSavingCategory] = useState(false);
    const [loading, setLoading] = useState(true);
    const token = localStorage.getItem('authToken');

    const handleApiError = (error) => {
        console.error('API Error:', error);
        alert(error.response?.data?.message || 'Something went wrong. Please try again.');
    };

    const getSingleCategory = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/category-data/${id}`);
            setCategoryData(response.data);
        } catch (error) {
            handleApiError(error);
            setTimeout(() => navigate('/admin/categories'), 2000);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (event) => {
        setCategoryData({
            ...categoryData,
            [event.target.name]: event.target.value,
        });
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            alert('Only JPG, PNG, or WebP images are allowed.');
            return;
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB limit
            alert('File size must be under 5MB.');
            return;
        }

        try {
            setImageUploading(true);
            const formData = new FormData();
            formData.append('image', file);

            const response = await axios.post(`${API_BASE_URL}/upload-prod-img`, formData);
            setCategoryData({ ...categoryData, image: response.data.path });
            alert('Image uploaded successfully!');
        } catch (error) {
            handleApiError(error);
        } finally {
            setImageUploading(false);
        }
    };

    const validateFields = () => {
        if (!categoryData.name.trim()) {
            alert('Category name is required');
            return false;
        }
        if (!categoryData.image.trim()) {
            alert('Category image is required');
            return false;
        }
        return true;
    };

    const handleUpdateCategory = async () => {
        if (!validateFields()) return;

        try {
            setSavingCategory(true);
            const response = await axios.patch(`${API_BASE_URL}/update-category/${id}`, categoryData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message);
            navigate('/admin/categories');
        } catch (error) {
            handleApiError(error);
        } finally {
            setSavingCategory(false);
        }
    };

    const handleBackButtonClick = () => {
        navigate('/admin/categories');
    };

    useEffect(() => {
        getSingleCategory();
    }, [id]);

    if (loading) {
        return (
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 'calc(100vh - 120px)' }}>
                    <LoadingScreen />
                    <p className="mt-3 text-muted small fw-semibold" style={{ letterSpacing: '0.5px' }}>Loading category details...</p>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1">EDIT CATEGORY</h4>
                                <p className="text-muted p-0 m-0">Update category information and image</p>
                            </div>
                            <button 
                                className="btn btn-outline-danger btn-sm rounded-0"
                                onClick={handleBackButtonClick}
                                title="Back to Categories"
                            >
                                <FaArrowLeft className="me-2" />
                                Back to Categories
                            </button>
                        </div>
                    </div>
                </div>

                <div className="row justify-content-center mt-4">
                    <div className="col-lg-6 col-md-8 col-sm-10">
                        <div className="card border-0 shadow-sm">
                            <div className="card-header bg-white border-0">
                                <h5 className="mb-0">
                                    <FaEdit className="text-danger me-2" />
                                    Category Information
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Category Name</label>
                                            <input
                                                type="text"
                                                className="form-control rounded-0"
                                                name="name"
                                                value={categoryData.name || ''}
                                                onChange={handleChange}
                                                placeholder="Enter category name"
                                            />
                                        </div>
                                        
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Category Image</label>
                                            <input
                                                type="file"
                                                className="form-control rounded-0"
                                                name="image"
                                                onChange={handleImageUpload}
                                                accept="image/*"
                                            />
                                            {imageUploading && (
                                                <div className="mt-2">
                                                    <FaSpinner className="spinner-border spinner-border-sm me-2" />
                                                    <small className="text-muted">Uploading image...</small>
                                                </div>
                                            )}
                                            {categoryData.image && !imageUploading && (
                                                <div className="mt-2">
                                                    <small className="text-success">
                                                        <FaImage className="me-1" />
                                                        Image uploaded successfully
                                                    </small>
                                                </div>
                                            )}
                                        </div>

                                        <button
                                            className="btn btn-danger rounded-0 w-100"
                                            disabled={savingCategory || imageUploading}
                                            onClick={handleUpdateCategory}
                                        >
                                            {savingCategory ? (
                                                <>
                                                    <FaSpinner className="spinner-border spinner-border-sm me-2" />
                                                    Updating Category...
                                                </>
                                            ) : (
                                                <>
                                                    <FaSave className="me-2" />
                                                    Update Category
                                                </>
                                            )}
                                        </button>
                                    </div>
                                    
                                    <div className="col-md-6">
                                        <div className="text-center">
                                            <label className="form-label fw-bold">Image Preview</label>
                                            <div className="border rounded p-3">
                                                {imageUploading ? (
                                                    <div className="d-flex align-items-center justify-content-center" style={{ height: '200px' }}>
                                                        <FaSpinner className="spinner-border spinner-border-sm me-2" />
                                                        <small>Uploading image...</small>
                                                    </div>
                                                ) : categoryData.image ? (
                                                    <img
                                                        src={`${API_BASE_URL}/${categoryData.image}`}
                                                        alt={categoryData.name}
                                                        className="img-fluid rounded"
                                                        style={{ maxHeight: '200px', width: 'auto' }}
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'block';
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="d-flex align-items-center justify-content-center text-muted" style={{ height: '200px' }}>
                                                        <div>
                                                            <FaImage className="fs-1 mb-2" />
                                                            <p className="mb-0">No image selected</p>
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="text-muted small mt-1" style={{ display: 'none' }}>
                                                    No image available
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
}

export default EditCategoryPage;
